import { useEffect, useRef, useState } from "react";
import { Send, Paperclip, Image as ImageIcon, Mic, Sparkles, ShieldAlert, MessageSquare, Plus, BookMarked, FileSearch } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";

const SUGGESTIONS = [
  "Explain money laundering in simple terms",
  "How does human trafficking recruitment work?",
  "What are the red flags in a phishing email?",
  "Summarize the UN Palermo Convention",
  "What is a shell company and how is it abused?",
  "What is deepfake voice fraud?",
];

const CAPABILITIES = [
  "Legal documents", "Organized crime", "Cybercrime", "Human trafficking",
  "Money laundering", "Fraud", "Identity theft", "Scam messages", "International law",
];

// Reference frames — public sources IOCRI cites in briefings
const REFERENCE_FRAMES = [
  { code: "UNODC",     name: "UN Office on Drugs and Crime",       url: "https://www.unodc.org/" },
  { code: "FATF",      name: "Financial Action Task Force",         url: "https://www.fatf-gafi.org/" },
  { code: "INTERPOL",  name: "International Criminal Police Org.",  url: "https://www.interpol.int/" },
  { code: "EUROPOL",   name: "European Union Agency for LEA",       url: "https://www.europol.europa.eu/" },
  { code: "OECD",      name: "Anti-Corruption & Integrity",         url: "https://www.oecd.org/corruption-integrity/" },
  { code: "GLOBAL OCI",name: "Global Organized Crime Index",        url: "https://ocindex.net/" },
];

// Naive keyword-based note synthesis from assistant messages
const NOTE_KEYS = [
  ["money laundering","AML"],["shell compan","Shell companies"],["phishing","Phishing"],
  ["deepfake","Deepfake"],["human trafficking","Trafficking"],["romance scam","Romance scam"],
  ["identity theft","Identity theft"],["ransomware","Ransomware"],["sanctions","Sanctions"],
  ["palermo","Palermo Convention"],["fatf","FATF standards"],["cybercrime","Cybercrime"],
  ["organized crime","Organized crime"],["fraud","Fraud"],["cryptocurrency","Crypto"],
];

function extractNotes(messages) {
  const seen = new Set();
  const notes = [];
  for (const m of messages) {
    if (m.role !== "assistant" || !m.content) continue;
    const lower = m.content.toLowerCase();
    for (const [key, label] of NOTE_KEYS) {
      if (lower.includes(key) && !seen.has(label)) {
        seen.add(label); notes.push(label);
      }
    }
  }
  return notes.slice(0, 10);
}

export default function AIAssistant() {
  const [sessionId, setSessionId] = useState(() => `s-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [imageB64, setImageB64] = useState(null);
  const [imageName, setImageName] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [latestSources, setLatestSources] = useState([]); // codes emitted by backend for most recent reply
  const scrollerRef = useRef(null);
  const fileRef = useRef(null);

  const loadSessions = () => api.get("/chat/sessions").then((r) => setSessions(r.data || [])).catch(() => {});
  useEffect(() => { loadSessions(); }, []);

  const openSession = async (sid) => {
    setSessionId(sid);
    try {
      const r = await api.get(`/chat/history/${sid}`);
      setMessages((r.data || []).map((m) => ({ role: m.role, content: m.content })));
    } catch { toast.error("Failed to load conversation."); }
  };

  const newChat = () => {
    setSessionId(`s-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
    setMessages([]);
  };

  useEffect(() => {
    scrollerRef.current?.scrollTo({ top: scrollerRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streaming]);

  const send = async (textOverride) => {
    const text = (textOverride ?? input).trim();
    if (!text || streaming) return;
    setInput("");
    const capturedImage = imageB64;
    setImageB64(null); setImageName("");
    setMessages((m) => [...m, { role: "user", content: text, image: capturedImage }]);
    setMessages((m) => [...m, { role: "assistant", content: "", streaming: true }]);
    setStreaming(true);

    try {
      let attempt = 0;
      let resp;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        resp = await fetch(`${API}/chat/stream`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ session_id: sessionId, message: text, image_base64: capturedImage }),
        });
        if (resp.ok || attempt >= 1) break;
        attempt += 1;
        await new Promise((r) => setTimeout(r, 800));
      }
      if (!resp.ok || !resp.body) throw new Error("Stream failed");
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let done = false;
      let buf = "";
      while (!done) {
        const { value, done: d } = await reader.read();
        done = d;
        if (value) {
          buf += decoder.decode(value, { stream: true });
          const events = buf.split("\n\n");
          buf = events.pop() || "";
          for (const ev of events) {
            if (ev.startsWith("event: done")) { done = true; break; }
            if (ev.startsWith("event: error")) { throw new Error(ev.slice(ev.indexOf("data:") + 5)); }
            if (ev.startsWith("event: sources")) {
              const line = ev.split("\n").find((l) => l.startsWith("data: "));
              if (line) {
                try {
                  const payload = JSON.parse(line.slice(6));
                  const codes = Array.isArray(payload.codes) ? payload.codes : [];
                  setLatestSources(codes);
                  setMessages((m) => {
                    const arr = [...m];
                    const last = arr[arr.length - 1];
                    if (last?.role === "assistant") last.sources = codes;
                    return arr;
                  });
                } catch { /* ignore */ }
              }
              continue;
            }
            if (ev.startsWith("data: ")) {
              const chunk = ev.slice(6);
              setMessages((m) => {
                const arr = [...m];
                const last = arr[arr.length - 1];
                if (last?.role === "assistant") last.content += chunk;
                return arr;
              });
            }
          }
        }
      }
    } catch {
      toast.error("AI request failed. Please try again.");
      setMessages((m) => m.slice(0, -1));
    } finally {
      setStreaming(false);
      setMessages((m) => m.map((x) => ({ ...x, streaming: false })));
      loadSessions();
    }
  };

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/(png|jpeg|jpg|webp)$/i.test(f.type.split("/").pop())) {
      toast.error("Only PNG, JPEG or WebP images are supported.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => { setImageB64(reader.result.toString().split(",")[1]); setImageName(f.name); };
    reader.readAsDataURL(f);
  };

  const voiceInput = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return toast.error("Voice input not supported in this browser.");
    const rec = new SR();
    rec.lang = "en-US"; rec.interimResults = false;
    rec.onresult = (e) => setInput((prev) => (prev ? prev + " " : "") + e.results[0][0].transcript);
    rec.onerror = () => toast.error("Voice recognition error.");
    rec.start();
    toast.info("Listening…");
  };

  const notes = extractNotes(messages);

  // A frame is "cited" if the backend attached ANY of its match codes to the latest reply.
  const isFrameCited = (frame) => {
    if (!latestSources || latestSources.length === 0) return false;
    return frame.match.some((k) => latestSources.includes(k));
  };
  const anyCited = latestSources.length > 0;

  return (
    <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-8" data-testid="ai-assistant-page">
      <SEO title="AI Research Desk" description="Ask an AI expert about scams, cybercrime, human trafficking, money laundering, fraud, identity theft and international law." path="/ai-assistant" image="ai" />

      {/* Header strip */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-sm border border-gold/40 bg-gold/[0.06] flex items-center justify-center">
            <Sparkles className="h-5 w-5 text-gold" />
          </div>
          <div>
            <div className="font-display text-2xl md:text-3xl text-white leading-tight">IOCRI Research Desk</div>
            <div className="text-[10px] font-mono tracking-[0.36em] text-white/50">EDUCATIONAL · SIGNAL-VERIFIED · v1.0</div>
          </div>
        </div>
        <button
          onClick={newChat}
          data-testid="new-chat-btn"
          className="hidden md:inline-flex items-center gap-2 rounded-sm border border-white/10 hover:border-gold/50 text-white/80 hover:text-white font-mono text-[11px] tracking-[0.24em] px-4 py-2"
        >
          <Plus className="h-4 w-4" /> NEW DOSSIER
        </button>
      </div>

      {/* Triptych */}
      <div className="desk-triptych">
        {/* LEFT — Research notes + recent sessions */}
        <aside className="desk-notes desk-panel" data-testid="desk-notes">
          <div className="desk-panel-title">RESEARCH NOTES</div>
          <div className="space-y-2 mb-6 min-h-[32px]">
            {notes.length === 0 && (
              <div className="text-white/30 text-[11px] font-mono italic">
                Ask the desk. Topics discovered will be pinned here.
              </div>
            )}
            {notes.map((n) => (
              <div key={n} className="flex items-center gap-2 border-l border-gold/40 pl-3 py-1.5">
                <FileSearch className="h-3 w-3 text-gold shrink-0" />
                <span className="text-white/85 text-xs">{n}</span>
              </div>
            ))}
          </div>

          <div className="desk-panel-title" style={{ marginTop: "auto" }}>RECENT DOSSIERS</div>
          <div className="space-y-1 flex-1 overflow-y-auto pr-1">
            {sessions.length === 0 && <div className="text-white/30 text-[11px] font-mono italic">No previous conversations</div>}
            {sessions.slice(0, 20).map((s) => (
              <button
                key={s.session_id}
                onClick={() => openSession(s.session_id)}
                data-testid={`session-${s.session_id}`}
                className={`w-full text-left rounded-sm border px-3 py-2 text-[11px] transition-colors ${
                  sessionId === s.session_id
                    ? "border-gold/50 bg-gold/[0.06] text-gold"
                    : "border-white/5 text-white/60 hover:border-gold/30 hover:text-white"
                }`}
              >
                <div className="flex items-center gap-2">
                  <MessageSquare className="h-3 w-3 shrink-0" />
                  <span className="truncate">{s.preview || "Untitled"}</span>
                </div>
              </button>
            ))}
          </div>
        </aside>

        {/* CENTER — chat */}
        <section className="desk-chat desk-panel" data-testid="desk-chat" style={{ padding: 0 }}>
          <div className="px-5 pt-5 pb-3 border-b border-white/5 flex items-center justify-between">
            <div className="desk-panel-title" style={{ marginBottom: 0 }}>ACTIVE INQUIRY</div>
            <div className="text-[10px] font-mono tracking-widest text-white/40">SESSION · {sessionId.slice(-6).toUpperCase()}</div>
          </div>

          <div ref={scrollerRef} className="flex-1 overflow-y-auto px-5 py-5 space-y-6 min-h-[400px]" data-testid="chat-scroller">
            {messages.length === 0 && (
              <div className="text-center py-10">
                <div className="font-display text-3xl md:text-4xl text-white leading-tight">
                  What signal do you need decoded?
                </div>
                <div className="mt-3 text-white/50 text-sm max-w-lg mx-auto">
                  Laws, scams, cybercrime, trafficking, laundering. Ask openly. IOCRI answers with citations.
                </div>
                <div className="mt-8 grid sm:grid-cols-2 gap-2 max-w-2xl mx-auto">
                  {SUGGESTIONS.slice(0, 4).map((s) => (
                    <button
                      key={s}
                      onClick={() => send(s)}
                      data-testid={`empty-suggest-${s.slice(0, 10)}`}
                      className="text-left rounded-sm border border-white/10 hover:border-gold/40 bg-black/40 px-4 py-3 text-sm text-white/80 hover:text-gold transition-colors"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`msg-${i}`}>
                <div
                  className={`max-w-[86%] rounded-sm px-5 py-4 border ${
                    m.role === "user"
                      ? "bg-white/[0.04] border-white/10 text-white"
                      : "bg-black/40 border-gold/25 text-white"
                  }`}
                >
                  {m.image && m.role === "user" && (
                    <img src={`data:image/png;base64,${m.image}`} alt="upload" className="mb-3 rounded-sm max-h-48 border border-white/10" />
                  )}
                  {m.role === "assistant" && (
                    <div className="text-[10px] font-mono tracking-[0.28em] text-gold mb-2">IOCRI DESK</div>
                  )}
                  <div className="whitespace-pre-wrap leading-relaxed text-[15px]">{m.content}</div>
                  {m.streaming && streaming && (
                    <div className="mt-2"><span className="typing-dot" /><span className="typing-dot" /><span className="typing-dot" /></div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Input */}
          <div className="border-t border-white/5 p-3">
            {imageName && (
              <div className="text-xs text-gold mb-2 flex items-center gap-2 font-mono px-2">
                <ImageIcon className="h-3 w-3" /> {imageName}
                <button onClick={() => { setImageB64(null); setImageName(""); }} className="text-white/50 hover:text-white ml-2" data-testid="clear-image">✕</button>
              </div>
            )}
            <div className="flex items-end gap-2">
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={handleFile} data-testid="file-input" />
              <button onClick={() => fileRef.current?.click()} data-testid="upload-btn" title="Upload image" className="h-10 w-10 rounded-sm border border-white/10 hover:border-gold/40 flex items-center justify-center text-white/70 hover:text-gold">
                <Paperclip className="h-4 w-4" />
              </button>
              <button onClick={voiceInput} data-testid="voice-btn" title="Voice input" className="h-10 w-10 rounded-sm border border-white/10 hover:border-gold/40 flex items-center justify-center text-white/70 hover:text-gold">
                <Mic className="h-4 w-4" />
              </button>
              <textarea
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Ask about scams, cybercrime, laundering, trafficking, laws…"
                rows={1}
                className="flex-1 resize-none bg-transparent text-white placeholder:text-white/40 outline-none px-3 py-2 min-h-[40px] max-h-40"
              />
              <Button
                onClick={() => send()}
                disabled={streaming || !input.trim()}
                data-testid="chat-send-btn"
                className="rounded-sm bg-gold text-black hover:bg-gold-light h-10 px-4 font-mono text-[11px] tracking-widest"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
            <div className="mt-3 flex items-start gap-2 text-[11px] text-white/50 px-1" data-testid="ai-disclaimer">
              <ShieldAlert className="h-3 w-3 text-gold shrink-0 mt-0.5" />
              <span>
                IOCRI answers are <span className="text-white/80">educational</span>. Not legal, medical, or law-enforcement guidance. In emergencies, contact your local authorities.
              </span>
            </div>
          </div>
        </section>

        {/* RIGHT — sources & capabilities */}
        <aside className="desk-sources desk-panel" data-testid="desk-sources">
          <div className="desk-panel-title">
            REFERENCE FRAMES {anyCited && <span className="ml-auto text-[9px] text-gold/70">CITED · {latestSources.length}</span>}
          </div>
          <div className="space-y-2 mb-6">
            {REFERENCE_FRAMES.map((s) => {
              const cited = isFrameCited(s);
              return (
                <a
                  key={s.code}
                  href={s.url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`source-${s.code.toLowerCase().replace(/\s/g,'-')}`}
                  className={`block rounded-sm border px-3 py-2.5 transition-all ${
                    cited
                      ? "border-gold/60 bg-gold/[0.08] shadow-[0_0_20px_-8px_rgba(0,212,255,0.6)]"
                      : anyCited
                        ? "border-white/5 opacity-40 hover:opacity-80"
                        : "border-white/5 hover:border-gold/25"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className={`text-[10px] font-mono tracking-[0.28em] ${cited ? "text-gold" : "text-white/60"}`}>{s.code}</div>
                    {cited && <span className="h-1.5 w-1.5 rounded-full bg-gold animate-pulse" />}
                  </div>
                  <div className="text-white text-xs mt-1">{s.name}</div>
                </a>
              );
            })}
            {anyCited && (
              <div className="text-[9px] font-mono tracking-widest text-white/40 pt-2">
                ◤ Highlighted frames were cited by the IOCRI Desk in the last response.
              </div>
            )}
          </div>

          <div className="desk-panel-title">SUGGESTED INQUIRIES</div>
          <div className="space-y-2 mb-6">
            {SUGGESTIONS.map((s) => (
              <button
                key={s}
                onClick={() => send(s)}
                data-testid={`suggest-${s.slice(0, 10)}`}
                className="w-full text-left text-[12px] text-white/75 hover:text-gold border border-white/5 hover:border-gold/40 rounded-sm px-3 py-2 transition-colors"
              >
                {s}
              </button>
            ))}
          </div>

          <div className="desk-panel-title">CAPABILITIES</div>
          <div className="flex flex-wrap gap-1.5">
            {CAPABILITIES.map((k) => (
              <span key={k} className="text-[10px] font-mono text-white/55 border border-white/10 rounded-sm px-2 py-1 tracking-widest">
                {k}
              </span>
            ))}
          </div>
        </aside>
      </div>

      {/* Info cards below */}
      <div className="mt-10 grid md:grid-cols-3 gap-4">
        {[
          [BookMarked, "Legal frameworks explained", "UNTOC, FATF, GDPR, FCPA, Interpol — cited in every answer."],
          [FileSearch, "Signal & message review", "Paste emails, SMS, or messages — the desk classifies the pattern."],
          [Sparkles,   "Educational, not advisory", "Guidance orients you. Decisions belong to professionals."],
        ].map(([Icon, t, d]) => (
          <div key={t} className="rounded-sm border border-white/5 bg-black/40 p-5">
            <Icon className="h-5 w-5 text-gold" />
            <div className="font-display text-lg text-white mt-3">{t}</div>
            <div className="text-white/55 text-sm mt-1">{d}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
